import { useState } from "react";

// ═══════════════════════════════════════════════════════
// Calculateur — Interférence entre 2 murs en gradins
// Règle : L = H / tan(45 + φ/2)
// Si D > L → pas d'interférence
// Si D ≤ L → surcharge mur supérieur à ajouter sur mur inférieur
// ═══════════════════════════════════════════════════════

const GV = {
  vert:"#2D7A2D", vertClair:"#00A651", vertLight:"#E8F5E9", vertMid:"#4CAF50",
  vertDark:"#1B5E20", blanc:"#FFFFFF", gris:"#F5F5F5", grisMid:"#757575",
  dark:"#1A1A1A", border:"#E0E0E0", orange:"#E8601A", orangeLight:"#FFF3ED",
  rouge:"#DC2626", rougeLight:"#FEF2F2", bleu:"#2563eb", bleuLight:"#EFF6FF",
};

function CalculateurDeuxMurs({ H_init, phi_init }) {
  const [params, setParams] = useState({
    H_total: H_init || 6.9,    // Hauteur totale du talus (m)
    repartition: 50,           // % hauteur mur supérieur (50 = égal)
    phi: phi_init || 20,       // Angle frottement sol dominant (°)
    D: 5.0,                    // Distance entre les deux murs (m)
    gamma_beton: 23, // Poids volumique béton mur supérieur (kN/m³)
    surcharge_existante: 30, // Surcharge déjà présente (kN/m²)
  });

  const sp = (k, v) => setParams(p => ({ ...p, [k]: parseFloat(v) || 0 }));

  // ── Calculs ──────────────────────────────────────────
  const H_sup = +(params.H_total * params.repartition / 100).toFixed(2);
  const H_inf = +(params.H_total - H_sup).toFixed(2);
  const phi_rad = params.phi * Math.PI / 180;
  const L = +(H_sup / Math.tan(Math.PI / 4 + phi_rad / 2)).toFixed(2);
  const interference = params.D <= L;

  // Largeur estimée semelle mur supérieur (règle pratique B ≈ 0.7×H)
  const B_sup = +(H_sup * 0.7).toFixed(2);
  // Poids du mur supérieur approximatif (kN/ml) = γ × profil ≈ γ × 0.13×H²
  const poids_mur_sup = +(params.gamma_beton * 0.13 * H_sup * H_sup).toFixed(1);
  // Surcharge équivalente sur largeur de diffusion B_sup
  const q_equivalent = +(poids_mur_sup / B_sup).toFixed(1);
  // Surcharge totale sur mur inférieur si interférence
  const q_total = +(params.surcharge_existante + q_equivalent).toFixed(1);

  // ── Styles ───────────────────────────────────────────
  const inpSt = {
    width:"100%", padding:"10px 12px", fontSize:14,
    border:`1.5px solid ${GV.border}`, borderRadius:8,
    outline:"none", boxSizing:"border-box", background:GV.blanc
  };
  const labelSt = {
    fontSize:12, fontWeight:700, color:GV.grisMid,
    textTransform:"uppercase", letterSpacing:0.8, display:"block", marginBottom:5
  };

  return (
    <div style={{ fontFamily:"'Segoe UI', system-ui, sans-serif", maxWidth:860, margin:"0 auto", padding:"0 4px" }}>

      {/* En-tête */}
      <div style={{ background:`linear-gradient(135deg, ${GV.vertDark}, ${GV.vert})`, borderRadius:14, padding:"20px 24px", marginBottom:20, color:GV.blanc }}>
        <div style={{ fontSize:18, fontWeight:800, marginBottom:4 }}>
          🏗️ Calculateur — 2 Murs en Gradins
        </div>
        <div style={{ fontSize:13, opacity:0.85, lineHeight:1.6 }}>
          Règle d'interférence : <strong>L = H / tan(45 + φ/2)</strong><br/>
          Si D &gt; L → pas d'interférence &nbsp;|&nbsp; Si D ≤ L → surcharge à ajouter
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>

        {/* ── Paramètres ── */}
        <div style={{ background:GV.blanc, border:`1px solid ${GV.border}`, borderRadius:12, padding:"20px 22px" }}>
          <div style={{ fontSize:13, fontWeight:800, color:GV.dark, marginBottom:16, letterSpacing:0.5, textTransform:"uppercase" }}>
            ⚙️ Paramètres du projet
          </div>

          {/* H total */}
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Hauteur totale du talus H_total (m)</label>
            <input type="number" min="2" max="20" step="0.1"
              value={params.H_total} onChange={e => sp("H_total", e.target.value)}
              style={{ ...inpSt, border:`1.5px solid ${GV.vert}` }}/>
            <div style={{ fontSize:11, color:GV.grisMid, marginTop:3 }}>
              H_sup = {H_sup}m · H_inf = {H_inf}m
            </div>
          </div>

          {/* Répartition */}
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>
              Répartition hauteur mur supérieur : {params.repartition}%
            </label>
            <input type="range" min="30" max="70" step="5"
              value={params.repartition} onChange={e => sp("repartition", e.target.value)}
              style={{ width:"100%", accentColor:GV.vert }}/>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:GV.grisMid }}>
              <span>30% ({+(params.H_total*0.3).toFixed(1)}m)</span>
              <span>50% — égal</span>
              <span>70% ({+(params.H_total*0.7).toFixed(1)}m)</span>
            </div>
          </div>

          {/* φ */}
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Angle de frottement φ du sol (°)</label>
            <input type="number" min="10" max="45" step="1"
              value={params.phi} onChange={e => sp("phi", e.target.value)}
              style={inpSt}/>
            <div style={{ fontSize:11, color:GV.grisMid, marginTop:3 }}>
              15–20° argile · 20–28° limon · 28–35° sable
            </div>
          </div>

          {/* D */}
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Distance entre les deux murs D (m)</label>
            <input type="number" min="0.5" max="30" step="0.5"
              value={params.D} onChange={e => sp("D", e.target.value)}
              style={{ ...inpSt, border:`1.5px solid ${interference ? GV.rouge : GV.vert}` }}/>
            <div style={{ fontSize:11, color: interference ? GV.rouge : GV.vert, marginTop:3, fontWeight:600 }}>
              {interference ? `⚠️ D=${params.D}m ≤ L=${L}m → INTERFÉRENCE` : `✅ D=${params.D}m > L=${L}m → OK`}
            </div>
          </div>

          {/* γ béton */}
          <div style={{ marginBottom:14 }}>
            <label style={labelSt}>Poids volumique béton mur supérieur γ (kN/m³)</label>
            <input type="number" min="20" max="26" step="0.5"
              value={params.gamma_beton} onChange={e => sp("gamma_beton", e.target.value)}
              style={inpSt}/>
          </div>

          {/* Surcharge existante */}
          <div style={{ marginBottom:4 }}>
            <label style={labelSt}>Surcharge existante derrière mur inférieur (kN/m²)</label>
            <input type="number" min="0" max="100" step="5"
              value={params.surcharge_existante} onChange={e => sp("surcharge_existante", e.target.value)}
              style={inpSt}/>
            <div style={{ fontSize:11, color:GV.grisMid, marginTop:3 }}>
              Trafic, entrepôt, etc. — 0 si aucune surcharge
            </div>
          </div>
        </div>

        {/* ── Résultats ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

          {/* Résultat principal */}
          <div style={{
            background: interference ? GV.rougeLight : GV.vertLight,
            border:`2px solid ${interference ? GV.rouge : GV.vert}`,
            borderRadius:12, padding:"18px 20px"
          }}>
            <div style={{ fontSize:14, fontWeight:800, color: interference ? GV.rouge : GV.vertDark, marginBottom:10 }}>
              {interference ? "⚠️ INTERFÉRENCE DÉTECTÉE" : "✅ PAS D'INTERFÉRENCE"}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                ["Hauteur mur supérieur H_sup", `${H_sup} m`],
                ["Hauteur mur inférieur H_inf", `${H_inf} m`],
                ["Zone d'influence L = H_sup/tan(45+φ/2)", `${L} m`],
                ["Distance entre murs D", `${params.D} m`],
                ["Verdict", interference ? `D(${params.D}) ≤ L(${L}) → Interférence` : `D(${params.D}) > L(${L}) → OK`],
              ].map(([k,v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:13, borderBottom:`1px solid ${interference ? "#fecaca" : "#bbf7d0"}`, paddingBottom:5 }}>
                  <span style={{ color:GV.grisMid }}>{k}</span>
                  <span style={{ fontWeight:700, color:GV.dark }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Surcharge à ajouter si interférence */}
          {interference && (
            <div style={{ background:"#FFF7ED", border:`1.5px solid ${GV.orange}`, borderRadius:12, padding:"16px 18px" }}>
              <div style={{ fontSize:13, fontWeight:800, color:GV.orange, marginBottom:10 }}>
                📐 Surcharge à ajouter sur le mur inférieur
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
                {[
                  ["Semelle mur supérieur B ≈ 0.7×H_sup", `${B_sup} m`],
                  ["Poids mur supérieur (estimé)", `${poids_mur_sup} kN/ml`],
                  ["Surcharge équivalente q = P/B", `${q_equivalent} kN/m²`],
                  ["Surcharge existante", `${params.surcharge_existante} kN/m²`],
                  ["SURCHARGE TOTALE À SAISIR DANS GEO5", `${q_total} kN/m²`],
                ].map(([k,v], i) => (
                  <div key={k} style={{
                    display:"flex", justifyContent:"space-between", fontSize:13,
                    borderBottom:`1px solid #fed7aa`, paddingBottom:5,
                    fontWeight: i === 4 ? 800 : 400,
                    color: i === 4 ? GV.orange : GV.dark
                  }}>
                    <span>{k}</span>
                    <span style={{ fontWeight:700 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:12, fontSize:12, color:"#92400E", lineHeight:1.7, background:"#FEF3C7", borderRadius:8, padding:"10px 12px" }}>
                <strong>Dans GEO5 Murs Cantilever (mur inférieur) :</strong><br/>
                Onglet Surcharges → Ajouter une surcharge bande de <strong>{q_total} kN/m²</strong><br/>
                Position x = distance depuis la face du mur inférieur jusqu'à la base du mur supérieur<br/>
                Longueur = largeur de la semelle du mur supérieur ({B_sup}m)
              </div>
            </div>
          )}

          {/* Schéma simplifié */}
          <div style={{ background:GV.blanc, border:`1px solid ${GV.border}`, borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:12, fontWeight:700, color:GV.grisMid, marginBottom:12, textTransform:"uppercase", letterSpacing:0.8 }}>
              📊 Schéma de principe
            </div>
            <svg viewBox="0 0 300 180" style={{ width:"100%", height:"auto" }}>
              {/* Terrain */}
              <rect x="0" y="160" width="300" height="20" fill="#c8a96e"/>

              {/* Mur supérieur */}
              <rect x="180" y={160 - H_sup * 18} width="16" height={H_sup * 18} fill="#2563eb"/>
              <rect x="155" y="150" width="55" height="12" fill="#2563eb" opacity="0.7"/>
              <text x="165" y={160 - H_sup * 18 - 5} fontSize="9" fill="#2563eb" fontWeight="bold">H_sup={H_sup}m</text>

              {/* Mur inférieur */}
              <rect x="60" y={160 - H_inf * 18} width="16" height={H_inf * 18} fill="#16a34a"/>
              <rect x="35" y="150" width="55" height="12" fill="#16a34a" opacity="0.7"/>
              <text x="38" y={160 - H_inf * 18 - 5} fontSize="9" fill="#16a34a" fontWeight="bold">H_inf={H_inf}m</text>

              {/* Zone d'influence */}
              <line x1="180" y1={160 - H_sup * 18} x2="76" y2="150" stroke={interference ? GV.rouge : GV.vert} strokeWidth="1.5" strokeDasharray="5,3"/>
              <text x="110" y="128" fontSize="8" fill={interference ? GV.rouge : GV.vert} fontWeight="bold">L={L}m</text>

              {/* Distance D */}
              <line x1="76" y1="170" x2="180" y2="170" stroke="#666" strokeWidth="1"/>
              <line x1="76" y1="165" x2="76" y2="175" stroke="#666" strokeWidth="1"/>
              <line x1="180" y1="165" x2="180" y2="175" stroke="#666" strokeWidth="1"/>
              <text x="118" y="178" fontSize="8" fill="#444" textAnchor="middle">D={params.D}m</text>

              {/* Talus */}
              <polygon points="196,160,300,160,300,0" fill="#e8d5b0" opacity="0.5"/>
              <text x="240" y="80" fontSize="9" fill="#8B6914" transform="rotate(-55,240,80)">Talus</text>
            </svg>
          </div>

          {/* Conseil pratique */}
          <div style={{ background:GV.bleuLight, border:`1px solid #bfdbfe`, borderRadius:10, padding:"12px 14px" }}>
            <div style={{ fontSize:12, fontWeight:700, color:GV.bleu, marginBottom:6 }}>💡 Conseil pratique</div>
            <div style={{ fontSize:12, color:"#1e40af", lineHeight:1.7 }}>
              Pour H_total = {params.H_total}m avec φ = {params.phi}°,
              la <strong>distance minimale recommandée</strong> entre les deux murs est <strong>D &gt; {L}m</strong>.<br/>
              Répartition optimale : murs égaux ({+(params.H_total/2).toFixed(1)}m / {+(params.H_total/2).toFixed(1)}m)
              sauf contrainte topographique.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CalculateurDeuxMurs;
